import React, { useContext, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import styles from "./SwatchesSelector.module.css";
import {
    Modal,
    Button,
    Spinner,
    Text,
    Select,
    RangeSlider,
    Collapsible,
    Icon,
    IndexTable,
    LegacyCard,
    DropZone,
} from "@shopify/polaris";

import {
    BundleTypes,
    fetchCollectionProductChunksWithOptionsMeta,
    fetchProductChunksWithOptionsMeta,
    findSwatchEntryForOption,
    findSwatchEntryForValue,
} from "../../apps/Bundles/constants";
import useIsMobile from "../../hooks/useIsMobile";
import { AppConfigContext } from "../../context/AppConfigContext";
import { StandardPreview } from "../../apps/Bundles/Standard/StandardBundle/StandardBundle";
import { MixMatchSectionalPreview } from "../../apps/Bundles/MixMatchSectional/MixMatchBundle/MixMatchSectionalBundle";
import {
    BundleBuilderConfigInterface,
    BuyNConfigInterface,
    BxgyConfigInterface,
    CombinedBundleConfigInterface,
    FBTConfigInterface,
    MixMatchConfigInterface,
    MixMatchSectionalConfigInterface,
    PlaceholderConfigInterface,
    ProductAddonsConfigInterface,
    ProductAddonsPopupConfigInterface,
    ProductSourceType,
    StandardConfigInterface,
    SwatchOption,
    VolumeConfigInterface,
} from "../../apps/Bundles/Types";
import { ChevronDownIcon, ChevronUpIcon, ImageIcon } from "@shopify/polaris-icons";
import NewCustomColorPicker from "../NewCustomColorPicker/NewCustomColorPicker";
import { getDefaultColorByOptionValue } from "../../utilities/constants";
import MediaLibrary from "../MediaLibrary/MediaLibrary";
import { VolumePreview } from "../../apps/Bundles/VolumeDiscount/VolumeBundle/VolumeBundle";
import { MixMatchPreview } from "../../apps/Bundles/MixMatch/MixMatchBundle/MixMatchBundle";
import { FBTPreview } from "../../apps/Bundles/FTB/FBTBundle/FBTBundle";
import { BxgyPreview } from "../../apps/Bundles/Bxgy/BxgyBundle/BxgyBundle";
import { ProductAddonsPreview } from "../../apps/Bundles/productAddOns/ProductAddonsBundle/ProductAddonsBundle";
import { ProductAddonsPopupPreview } from "../../apps/Bundles/productAddOnsPopup/ProductAddonsPopupBundle/ProductAddonsPopupBundle";

import { postData } from "../../utilities/networkCalls";
import config from "../../config";
import { BundleBuilderPreview } from "../../apps/Bundles/BundleBuilder/BundleBuilderBundle/BundleBuilderBundle";
import { BuyNPreview } from "../../apps/Bundles/BuyN/BuyNbundle/BuyNbundle";
import { PlaceholderPreview } from "../../apps/Bundles/Placeholder/PlaceholderBundle/PlaceholderBundle";
import { SHOPS_WITH_MANY_VARIANTS } from "../../apps/constants";

type SwatchApplyPayload = Partial<
    | StandardConfigInterface
    | VolumeConfigInterface
    | BxgyConfigInterface
    | MixMatchConfigInterface
    | MixMatchSectionalConfigInterface
    | FBTConfigInterface
    | ProductAddonsConfigInterface
    | ProductAddonsPopupConfigInterface
>;
type OnApplyType = (updated: SwatchApplyPayload) => void;

interface SwatchSelectorTypes {
    open: boolean;
    productSource: ProductSourceType;
    setOpen: (val: boolean) => void;
    bundleConfig: CombinedBundleConfigInterface;
    onApply: OnApplyType;
    SwatchesBundleConfigTouchedRef: React.MutableRefObject<Record<string, string>>;
}

// interface SwatchSettings {
//     swatchSize: number; // from range
//     swatchShape: "circle" | "square" | "rounded";
// }

export interface SwatchesProductsMap {
    [productId: string]: {
        [optionName: string]: {
            layout: string;
            /** Locale-independent gid://shopify/ProductOption/... — option names are translated per market. */
            optionId?: string;
            values: {
                [optionValue: string]: {
                    color: string;
                    image: string;
                    /** Position of the value inside the option, used when translated strings don't match. */
                    index?: number;
                };
            };
        };
    };
}

type swatchProduct = {
    productId: string;
    productTitle: string;
    optionId?: string;
    valueIndex?: number;

    color: string;
    image: string;
};

type GroupedByOption = {
    [optionName: string]: {
        [optionValue: string]: {
            assignedValue: {
                color: string;
                image: string;
            };
            products: swatchProduct[];
        };
    };
};

type OptionValueItemProps = {
    optionValue: string;
    group: {
        assignedValue: { color: string; image: string };
        products: {
            productId: string;
            productTitle: string;
            color: string;
            image: string;
        }[];
    };
    selectedOption: string;
    optionLayout: string;
    handleSwatchUpdate: ({
        level,
        optionName,
        optionValue,
        newValue,
        productId,
        productTitle,
    }: SwatchUpdateProps) => void;
};

export interface ProductWithOptionsAndMeta {
    id: string;
    title: string;
    options: {
        id: string;
        name: string;
        values: string[];
    }[];
    metafield?: { value: string; type: string };
}

// interface ProductChunkResult {
//     products: ProductWithOptionsAndMeta[];
//     nextCursor: string | null;
//     hasNext: boolean;
// }

interface SwatchUpdateProps {
    level: "parent" | "product";
    optionName: string;
    optionValue: string;
    valueType: "color" | "image";
    newValue: string;
    productId?: string;
    productTitle?: string;
}

const SwatchesSelector = ({
    open,
    setOpen,
    productSource,
    bundleConfig,
    onApply,
    SwatchesBundleConfigTouchedRef,
}: SwatchSelectorTypes) => {
    const [loading, setLoading] = useState(false);
    const [fetchedProductsWithOptions, setFetchedProductsWithOptions] = useState<ProductWithOptionsAndMeta[]>([]);
    const { isMobile } = useIsMobile();
    const appConfigContext = useContext(AppConfigContext);
    const { sft } = appConfigContext;
    const [optionLayouts, setOptionLayouts] = useState<Record<string, string>>({}); // { Color: 'color_swatch', Size: 'image_dropdown' ... }
    const [groupedOptions, setGroupedOptions] = useState<GroupedByOption>({});
    const [localBundleConfig, setLocalBundleConfig] = useState(bundleConfig);

    const optionNames = useMemo(() => {
        const set = new Set<string>();

        fetchedProductsWithOptions.forEach((prod) => {
            prod.options.forEach((opt) => {
                set.add(opt.name);
            });
        });

        return Array.from(set).map((name) => ({ label: name, value: name }));
    }, [fetchedProductsWithOptions]);

    const [selectedOption, setSelectedOption] = useState<{ label: string; value: string }>({ label: "", value: "" });

    const handleClose = () => {
        setOpen(false);
    };

    const onSelect = () => {
        setOpen(false);

        onApply({
            swatchShape: localBundleConfig.swatchShape,
            swatchSize: localBundleConfig.swatchSize,
            swatchOptions: localBundleConfig.swatchOptions,
            swatchesProductsMap: buildSwatchesProductsMap(groupedOptions, optionLayouts),
        });
    };

    const handleRangeSlider = (value: number) => {
        setLocalBundleConfig((prev) => ({
            ...prev,
            swatchSize: value,
        }));
    };

    const handleShapeSelector = (value: "circle" | "rounded" | "square") => {
        setLocalBundleConfig((prev) => ({
            ...prev,
            swatchShape: value,
        }));
    };

    // -------------------------------------------------------
    const handleLayoutChange = (
        optionName: {
            label: string;
            value: string;
        },
        layout: string,
    ) => {
        setOptionLayouts((prev) => ({
            ...prev,
            [optionName.value]: layout, //  Update layout for this specific option
        }));

        // Mark that user has changed bundle config manually
        // This will make product grouping logic ignore metafield and use local swatchOptions
        SwatchesBundleConfigTouchedRef.current[optionName.value] = layout;
    };

    const fetchAllProductIds = async (): Promise<ProductWithOptionsAndMeta[]> => {
        const allProducts: ProductWithOptionsAndMeta[] = [];
        let hasNextPageExists = true;
        let cursor: string | null = null;

        while (hasNextPageExists) {
            const { products, endCursor, hasNextPage } = await fetchProductChunksWithOptionsMeta(
                sft,
                setLoading,
                cursor,
                false,
            );

            allProducts.push(...products);
            cursor = endCursor;
            hasNextPageExists = hasNextPage;
        }

        return allProducts;
    };

    const fetchProductsFromCollections = async (collectionIds: string[]): Promise<ProductWithOptionsAndMeta[][]> => {
        const result: ProductWithOptionsAndMeta[][] = [];

        for (const collectionId of collectionIds) {
            const products: ProductWithOptionsAndMeta[] = [];
            let hasNextPage = true;
            let cursor: string | null = null;
            while (hasNextPage) {
                const {
                    products: chunk,
                    nextCursor,
                    hasNext,
                } = await fetchCollectionProductChunksWithOptionsMeta(collectionId, sft, setLoading, false, cursor);

                products.push(...chunk);
                cursor = nextCursor;
                hasNextPage = hasNext;
            }

            result.push(products); // products group by collection
        }

        return result; // returns array of arrays
    };

    const groupProductsByOption = (
        products: ProductWithOptionsAndMeta[],
        swatchOptions?: SwatchOption[],
    ): GroupedByOption => {
        const grouped: GroupedByOption = {};
        products.forEach((product) => {
            const metaOptions = product.metafield?.value && JSON.parse(product.metafield.value)?.options;

            product.options.forEach((option, optionIndex) => {
                const optionName = option.name;
                if (!grouped[optionName]) grouped[optionName] = {};

                option.values.forEach((valueName, valueIndex) => {
                    const matchedSwatch = findSwatchEntryForOption(
                        swatchOptions,
                        option,
                        optionIndex,
                        product.options.length,
                    );

                    // Check metafield color/image
                    const matchedMetaOption = findSwatchEntryForOption(
                        metaOptions,
                        option,
                        optionIndex,
                        product.options.length,
                    );

                    const metaSwatch = findSwatchEntryForValue(
                        matchedMetaOption?.swatchValues,
                        valueName,
                        valueIndex,
                        option.values.length,
                    );

                    const colorObj = findSwatchEntryForValue(
                        matchedSwatch?.colors,
                        valueName,
                        valueIndex,
                        option.values.length,
                    );
                    const imageObj = findSwatchEntryForValue(
                        matchedSwatch?.images,
                        valueName,
                        valueIndex,
                        option.values.length,
                    );

                    let productColor = "";
                    let productImage = "";

                    //  Priority logic based on user touched state
                    if (SwatchesBundleConfigTouchedRef.current[optionName]) {
                        // If user has touched bundle config , give priority to swatchOptions (ignore metafield)
                        productColor = colorObj?.color || getDefaultColorByOptionValue(valueName);
                        productImage = imageObj?.image || "";
                    } else {
                        //  Default : give priority to metafield first, then swatchOptions fallback
                        productColor = metaSwatch?.color || colorObj?.color || getDefaultColorByOptionValue(valueName);
                        productImage = metaSwatch?.image || imageObj?.image || "";
                    }

                    if (!grouped[optionName][valueName]) {
                        grouped[optionName][valueName] = {
                            assignedValue: {
                                color: colorObj?.color || getDefaultColorByOptionValue(valueName),
                                image: imageObj?.image || "",
                            },
                            products: [],
                        };
                    }

                    // Add current product entry to that value group
                    grouped[optionName][valueName].products.push({
                        productId: product.id,
                        productTitle: product.title,
                        optionId: option.id,
                        valueIndex,
                        color: colorObj?.products?.find((p) => p.productId === product.id)?.color || productColor || "",
                        image: imageObj?.products?.find((p) => p.productId === product.id)?.image || productImage || "",
                    });
                });
            });
        });

        return grouped;
    };

    const handleSwatchUpdate = ({
        level,
        optionName,
        optionValue,
        valueType,
        newValue,
        productId,
        productTitle,
    }: SwatchUpdateProps) => {
        setGroupedOptions((prevGroupedOptions) => {
            const updatedGroupedOptions = { ...prevGroupedOptions };

            const currentOptionGroup = updatedGroupedOptions[optionName]?.[optionValue];
            if (!currentOptionGroup) return prevGroupedOptions;

            if (level === "parent") {
                // Update parent-level assigned value
                if (valueType === "color") {
                    currentOptionGroup.assignedValue.color = newValue;
                } else {
                    currentOptionGroup.assignedValue.image = newValue;
                }

                // Reset all product-level overrides when parent is changed
                currentOptionGroup.products = currentOptionGroup.products.map((product) => ({
                    ...product,
                    ...(valueType === "color" ? { color: "" } : {}),
                    ...(valueType === "image" ? { image: "" } : {}),
                }));
            }

            if (level === "product" && productId && productTitle) {
                const existingProduct = currentOptionGroup.products.find((product) => product.productId === productId); // ✅ check if product already exists

                if (existingProduct) {
                    currentOptionGroup.products = currentOptionGroup.products.map((product) =>
                        product.productId === productId
                            ? {
                                  ...product,
                                  ...(valueType === "color" ? { color: newValue } : {}),
                                  ...(valueType === "image" ? { image: newValue } : {}),
                              }
                            : product,
                    );
                } else {
                    currentOptionGroup.products.push({
                        productId: productId,
                        productTitle: productTitle,
                        color: valueType === "color" ? newValue : "",
                        image: valueType === "image" ? newValue : "",
                    });
                }
            }

            return updatedGroupedOptions;
        });
    };

    const buildSwatchOptions = (
        groupedOptions: GroupedByOption,
        optionLayouts: Record<string, string>,
    ): SwatchOption[] => {
        const swatchOptions: SwatchOption[] = [];

        // loop over each option name like "Color", "Size"
        Object.entries(groupedOptions).forEach(([optionName, optionValues]) => {
            const swatchType = optionLayouts[optionName]; // get selected layout for this option
            if (!swatchType) return; // skip if layout not selected

            const colors: SwatchOption["colors"] = [];
            const images: SwatchOption["images"] = [];

            // loop over each option value like "Red", "Blue", "Small"
            Object.entries(optionValues).forEach(([valueName, valueGroup]) => {
                const { color, image } = valueGroup.assignedValue;

                // Only include products that have a DIFFERENT color/image than the parent (actual overrides)
                const mappedColorProducts = valueGroup.products
                    .filter((p) => p?.color && p.color !== color)
                    .map((p) => ({
                        productId: p.productId,
                        color: p.color,
                    }));

                const mappedImageProducts = valueGroup.products
                    .filter((p) => p?.image && p.image !== image)
                    .map((p) => ({
                        productId: p.productId,
                        image: p.image,
                    }));

                //  Push color object if parent or any products have color
                if (color || mappedColorProducts.length > 0) {
                    const colorObj: any = {
                        name: valueName,
                    };
                    if (color) colorObj.color = color;
                    if (mappedColorProducts.length > 0) colorObj.products = mappedColorProducts;
                    colors.push(colorObj);
                }

                // Push image object if parent or any products have image
                if (image || mappedImageProducts.length > 0) {
                    const imageObj: any = {
                        name: valueName,
                    };
                    if (image) imageObj.image = image;
                    if (mappedImageProducts.length > 0) imageObj.products = mappedImageProducts;
                    images.push(imageObj);
                }
            });

            // One entry covers every product that has this option, so keep all of their option ids.
            const optionIds = Array.from(
                new Set(
                    Object.values(optionValues)
                        .flatMap((valueGroup) => valueGroup.products.map((prod) => prod.optionId))
                        .filter(Boolean) as string[],
                ),
            );

            swatchOptions.push({
                name: optionName,
                optionIds,
                swatchType,
                colors,
                images,
            });
        });

        return swatchOptions;
    };

    const buildSwatchesProductsMap = (
        groupedByOption: GroupedByOption,
        layoutMap: Record<string, string>,
    ): SwatchesProductsMap => {
        const result: SwatchesProductsMap = {};

        Object.entries(groupedByOption).forEach(([optionName, optionValues]) => {
            const layout = layoutMap[optionName]; // get layout for option
            if (!layout) return;

            Object.entries(optionValues).forEach(([optionValue, { assignedValue, products }], valuePosition) => {
                products.forEach((prod) => {
                    const hasColorOverride = prod.color && prod.color !== assignedValue.color;
                    const hasImageOverride = prod.image && prod.image !== assignedValue.image;

                    // Always include every product — layout/displayType must be written to ALL product metafields
                    // even if they have no per-product color/image override
                    if (!result[prod.productId]) result[prod.productId] = {};
                    if (!result[prod.productId][optionName])
                        result[prod.productId][optionName] = { layout, optionId: prod.optionId, values: {} };

                    result[prod.productId][optionName].values[optionValue] = {
                        color: hasColorOverride ? prod.color : assignedValue.color || "",
                        image: hasImageOverride ? prod.image : assignedValue.image || "",
                        index: prod.valueIndex ?? valuePosition,
                    };
                });
            });
        });

        return result;
    };

    // Whenever groupedOptions or optionLayouts change,regenerate swatchOptions and update localBundleConfig

    useEffect(() => {
        const hasValidData = Object.keys(groupedOptions).length > 0 && Object.keys(optionLayouts).length > 0;

        if (!hasValidData) return; // don't run on first empty render

        const newSwatchOptions = buildSwatchOptions(groupedOptions, optionLayouts);

        setLocalBundleConfig((prev) => ({
            ...prev,
            swatchOptions: newSwatchOptions,
        }));
    }, [groupedOptions, optionLayouts]);

    console._log("swatchOptions::", localBundleConfig?.swatchOptions, groupedOptions, fetchedProductsWithOptions);

    useEffect(() => {
        const fetchProducts = async () => {
            let products: ProductWithOptionsAndMeta[] = [];

            if (productSource?.type !== "all" && (!productSource?.value || productSource?.value.length === 0)) {
                setFetchedProductsWithOptions([]);
                return;
            }

            if (productSource.type === "specific") {
                products = productSource.value
                    .filter((p) => p?.id)
                    .map((p) => ({
                        id: p.id,
                        options: p.options || [],
                        title: p.title || "",
                        metafield: p.metafield,
                    }));
            } else if (productSource.type === "collection") {
                const validCollections = productSource.value.filter((id: string) => !!id);
                if (!validCollections.length) {
                    setFetchedProductsWithOptions([]);
                    return;
                }
                const results = await fetchProductsFromCollections(validCollections);
                products = results.flat();
            } else if (productSource.type === "all") {
                // Skip fetching all products for shops with too many variants
                if (SHOPS_WITH_MANY_VARIANTS.includes(shopify.config.shop || "")) {
                    setFetchedProductsWithOptions([]);
                    return;
                }
                products = await fetchAllProductIds();
            } else if (productSource.type === "exclude") {
                const allProducts = await fetchAllProductIds();
                const excludedIds = productSource.value || [];
                products = allProducts.filter((p: any) => !excludedIds.includes(p.id)); // ✅ filtered
            } else if (productSource.type === "multi") {
                const allProducts: ProductWithOptionsAndMeta[] = [];

                for (const section of productSource.value) {
                    if (section.selectionType === "specific" && section.products?.length) {
                        allProducts.push(
                            ...(section.products.map((p) => ({
                                id: p.id,
                                options: p.options || [],
                                metafield: p.metafield,
                                title: p.title,
                            })) || []),
                        );
                    }

                    if (section.selectionType === "collection" && section.collectionIds?.length) {
                        const res = await fetchProductsFromCollections(section.collectionIds);
                        res.forEach((arr) => allProducts.push(...arr));
                    }
                }

                const seen = new Set();
                products = allProducts.filter((p) => {
                    if (seen.has(p.id)) return false;
                    seen.add(p.id);
                    return true;
                });
            }

            // filter out products with useless options
            const usefulProducts = products.filter((p) => {
                const onlyOption =
                    p.options?.length === 1 &&
                    p.options[0].name === "Title" &&
                    p.options[0].values.length === 1 &&
                    p.options[0].values[0] === "Default Title";
                return !onlyOption;
            });

            setFetchedProductsWithOptions(usefulProducts);
        };

        fetchProducts();
    }, [productSource]);

    useEffect(() => {
        const grouped = groupProductsByOption(fetchedProductsWithOptions, localBundleConfig.swatchOptions);
        setGroupedOptions(grouped);

        const layoutMap: Record<string, string> = {};

        // 1️. Set all optionNames to default "dropdown"
        optionNames.forEach(({ value }) => {
            layoutMap[value] = "dropdown";
        });

        // 2️. Override with actual values if available from saved config
        if (localBundleConfig?.swatchOptions?.length) {
            localBundleConfig.swatchOptions.forEach((opt) => {
                layoutMap[opt.name] = opt.swatchType;
            });
        }

        setOptionLayouts(layoutMap);
    }, [fetchedProductsWithOptions, optionNames]);

    console._log("fetchedProductsWithOptions", fetchedProductsWithOptions, groupedOptions, optionLayouts);

    // when optionNames are first fetched, auto-select the first one
    useEffect(() => {
        if (optionNames.length > 0 && !selectedOption.value) {
            const first = optionNames[0];
            setSelectedOption(first); // set first available option as selected
        }
    }, [optionNames]);

    const updatedBundleConfig = {
        ...localBundleConfig,
        SwatchesBundleConfigTouchedRef: SwatchesBundleConfigTouchedRef,
        source: "modalPopup",
    };

    return (
        <div className={styles.modalWrapper}>
            <Modal
                open={open}
                onScrolledToBottom={() => {}}
                sectioned
                onClose={handleClose}
                title="Add Swatches to Variant Picker"
                limitHeight={false}
                size="large"
                instant
                primaryAction={{
                    content: "Apply",
                    onAction: onSelect,
                }}
                secondaryActions={[
                    {
                        content: "Cancel",
                        onAction: handleClose,
                    },
                ]}
            >
                <div className={styles.swatchModalContainer}>
                    {loading ? (
                        <SwatchesModalLoader />
                    ) : (
                        <div className={isMobile ? styles.layoutColumn : styles.layoutRow}>
                            {/* Left Panel */}
                            <div className={styles.editorPanel}>
                                {/* Swatches Section Title */}
                                <Text variant="headingMd" as="h3">
                                    Swatches
                                </Text>

                                {/* Select Row - Option & Style */}
                                <div className={styles.selectRow}>
                                    <div className={styles.selectWrapper}>
                                        <Select
                                            label="Option Type"
                                            options={optionNames}
                                            onChange={(value) => {
                                                const matched = optionNames.find((opt) => opt.value === value);
                                                if (matched) setSelectedOption(matched);
                                            }}
                                            value={selectedOption.value}
                                        />
                                    </div>
                                    <div className={styles.selectWrapper}>
                                        <Select
                                            label="Option Layout"
                                            disabled={!selectedOption?.value}
                                            options={[
                                                { label: "Default Dropdown", value: "default" },
                                                { label: "Color Swatch", value: "color_swatch" },
                                                { label: "Image Swatch", value: "image_swatch" },
                                                { label: "Color Dropdown", value: "color_dropdown" },
                                                { label: "Image Dropdown", value: "image_dropdown" },
                                                { label: "label Swatch", value: "label" },
                                            ]}
                                            onChange={(layout) => handleLayoutChange(selectedOption, layout)}
                                            value={optionLayouts[selectedOption.value]}
                                        />
                                    </div>
                                </div>

                                {/* View Mode Toggle Popover (Options vs Products) */}
                                <div className={styles.viewToggleContainer}>
                                    {/* Polaris Popover or button group here */}
                                    {/* // TODO: Add view toggle between Option-based and Product-based */}
                                </div>

                                {/* Collapsible Option Rows */}
                                {selectedOption && groupedOptions[selectedOption.value] ? (
                                    ["color_swatch", "image_swatch", "color_dropdown", "image_dropdown"].includes(
                                        optionLayouts[selectedOption.value],
                                    ) ? ( // dont show table in case of default cases
                                        <LegacyCard>
                                            <div>
                                                <IndexTable
                                                    resourceName={{
                                                        singular: "Option Value",
                                                        plural: "Option Values",
                                                    }}
                                                    itemCount={Object.keys(groupedOptions[selectedOption.value]).length}
                                                    headings={[{ title: "Option Values" }]}
                                                    selectable={false}
                                                >
                                                    {Object.entries(groupedOptions[selectedOption.value]).map(
                                                        ([optionValue, group], index) => (
                                                            <IndexTable.Row
                                                                id={optionValue}
                                                                key={optionValue}
                                                                position={index}
                                                            >
                                                                <IndexTable.Cell>
                                                                    <OptionValueItem
                                                                        optionValue={optionValue}
                                                                        group={group}
                                                                        selectedOption={selectedOption.label}
                                                                        optionLayout={
                                                                            optionLayouts[selectedOption.value]
                                                                        }
                                                                        handleSwatchUpdate={handleSwatchUpdate}
                                                                    />
                                                                </IndexTable.Cell>
                                                            </IndexTable.Row>
                                                        ),
                                                    )}
                                                </IndexTable>
                                            </div>
                                        </LegacyCard>
                                    ) : null
                                ) : (
                                    <div className={styles.noOptionsMessage}>
                                        <Text variant="headingSm" as="h4">
                                            No Options Found
                                        </Text>
                                        <Text as="h4" variant="bodyMd" tone="subdued">
                                            Selected products have no custom options. Please ensure they include option
                                            other than 'Default Title'.
                                        </Text>
                                    </div>
                                )}

                                <div className={styles.settingsContainer}>
                                    {/* Settings Section Title */}
                                    <Text variant="headingMd" as="h3">
                                        Settings
                                    </Text>

                                    {/* Settings Inputs Row - Swatch Size (Range) & Shape */}
                                    <div className={styles.settingsRow}>
                                        <div className={styles.rangeWrapper}>
                                            <RangeSlider
                                                disabled={!selectedOption?.value}
                                                label="Swatch Size"
                                                min={10}
                                                max={100}
                                                step={1}
                                                value={localBundleConfig?.swatchSize}
                                                onChange={handleRangeSlider}
                                            />
                                        </div>
                                        <div className={styles.selectWrapper}>
                                            <Select
                                                disabled={!selectedOption?.value}
                                                label="Swatch Shape"
                                                options={[
                                                    { label: "Circle", value: "circle" },
                                                    { label: "Square", value: "square" },
                                                    { label: "Rounded", value: "rounded" },
                                                ]}
                                                onChange={handleShapeSelector}
                                                value={localBundleConfig?.swatchShape}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Panel */}
                            <div className={styles.previewPanel} id="swatchesModalRightSectionId">
                                {(() => {
                                    switch (localBundleConfig.bundle_type) {
                                        case BundleTypes.STANDARD:
                                            return (
                                                <StandardPreview
                                                    appConfigContext={appConfigContext}
                                                    bundleConfig={updatedBundleConfig as StandardConfigInterface}
                                                    targetForm={null}
                                                />
                                            );

                                        case BundleTypes.VOLUME_DISCOUNT:
                                            return (
                                                <VolumePreview
                                                    pId={0}
                                                    appConfigContext={appConfigContext}
                                                    bundleConfig={updatedBundleConfig as VolumeConfigInterface}
                                                />
                                            );

                                        case BundleTypes.MIX_MATCH:
                                            return (
                                                <MixMatchPreview
                                                    appConfigContext={appConfigContext}
                                                    bundleConfig={updatedBundleConfig as MixMatchConfigInterface}
                                                />
                                            );
                                        case BundleTypes.BUY_N:
                                            return (
                                                <BuyNPreview
                                                    appConfigContext={appConfigContext}
                                                    bundleConfig={updatedBundleConfig as BuyNConfigInterface}
                                                />
                                            );
                                        case BundleTypes.FBT:
                                            return (
                                                <FBTPreview
                                                    appConfigContext={appConfigContext}
                                                    bundleConfig={updatedBundleConfig as FBTConfigInterface}
                                                    previewType={null}
                                                />
                                            );
                                        case BundleTypes.BXGY:
                                            return (
                                                <BxgyPreview
                                                    appConfigContext={appConfigContext}
                                                    bundleConfig={updatedBundleConfig as BxgyConfigInterface}
                                                />
                                            );

                                        case BundleTypes.PRODUCT_ADDONS:
                                            return (
                                                <ProductAddonsPreview
                                                    appConfigContext={appConfigContext}
                                                    bundleConfig={updatedBundleConfig as ProductAddonsConfigInterface}
                                                />
                                            );

                                        case BundleTypes.PRODUCT_ADDONS_POPUP:
                                            return (
                                                <ProductAddonsPopupPreview
                                                    appConfigContext={appConfigContext}
                                                    bundleConfig={
                                                        updatedBundleConfig as ProductAddonsPopupConfigInterface
                                                    }
                                                />
                                            );

                                        case BundleTypes.MIX_MATCH_SECTIONAL:
                                            return (
                                                <MixMatchSectionalPreview
                                                    appConfigContext={appConfigContext}
                                                    bundleConfig={
                                                        updatedBundleConfig as MixMatchSectionalConfigInterface
                                                    }
                                                />
                                                // <></>
                                            );
                                        case BundleTypes.BUNDLE_BUILDER:
                                            return (
                                                <BundleBuilderPreview
                                                    appConfigContext={appConfigContext}
                                                    bundleConfig={updatedBundleConfig as BundleBuilderConfigInterface}
                                                    previewType={1}
                                                />
                                                // <></>
                                            );
                                        case BundleTypes.BB_PLACEHOLDER:
                                            return (
                                                <PlaceholderPreview
                                                    appConfigContext={appConfigContext}
                                                    bundleConfig={updatedBundleConfig as PlaceholderConfigInterface}
                                                    previewType={1}
                                                    targetForm={null}
                                                    placement="swatches"
                                                />
                                                // <></>
                                            );

                                        default:
                                            return null;
                                    }
                                })()}
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default SwatchesSelector;

const OptionValueItem = ({
    optionValue,
    group,
    selectedOption,
    optionLayout,
    handleSwatchUpdate,
}: OptionValueItemProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const isImageLayout = ["image_dropdown", "image_swatch"].includes(optionLayout);
    const isColorLayout = ["color_dropdown", "color_swatch"].includes(optionLayout);

    console._log("optionValue", optionValue, group);

    return (
        <div className={styles.optionValueContainer}>
            {/* Header */}
            <div className={styles.optionValueHeader}>
                <div className={styles.optionValueTitle}>{optionValue}</div>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    {/* Parent Swatch Picker */}
                    {isColorLayout && (
                        <div onClick={(e) => e.stopPropagation()}>
                            <NewCustomColorPicker
                                hexColor={group.assignedValue.color}
                                setHexColor={(val) => {
                                    handleSwatchUpdate({
                                        level: "parent",
                                        optionName: selectedOption,
                                        optionValue,
                                        valueType: "color",
                                        newValue: val,
                                    });
                                }}
                                label=""
                                props={null}
                            />
                        </div>
                    )}

                    {isImageLayout && (
                        <div onClick={(e) => e.stopPropagation()}>
                            <ImageSelector
                                imageUrl={group.assignedValue.image || ""}
                                onSelectImage={(val) => {
                                    handleSwatchUpdate({
                                        level: "parent",
                                        optionName: selectedOption,
                                        optionValue,
                                        newValue: val,
                                        valueType: "image",
                                    });
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
            <span onClick={() => setIsOpen(!isOpen)} className={styles.configureToggle}>
                Configure product wise
                <Icon source={isOpen ? ChevronUpIcon : ChevronDownIcon} tone="subdued" />
            </span>

            <Collapsible id={optionValue} open={isOpen} transition={{ duration: "300ms", timingFunction: "ease" }}>
                <div className={styles.productLevelContainer}>
                    {group.products.map((product, idx) => (
                        <div key={idx} className={styles.productRow}>
                            <div className={styles.productTitleWithPicker}>
                                <span className={styles.productTitle}>{product.productTitle}</span>
                                {isColorLayout && (
                                    <NewCustomColorPicker
                                        hexColor={product.color || group.assignedValue.color}
                                        setHexColor={(val) => {
                                            handleSwatchUpdate({
                                                level: "product",
                                                optionName: selectedOption,
                                                optionValue,
                                                newValue: val,
                                                valueType: "color",
                                                productId: product.productId,
                                                productTitle: product.productTitle,
                                            });
                                        }}
                                        label=""
                                        props={null}
                                    />
                                )}
                                {isImageLayout && (
                                    <ImageSelector
                                        imageUrl={product?.image || group.assignedValue.image || ""}
                                        onSelectImage={(val) => {
                                            handleSwatchUpdate({
                                                level: "product",
                                                optionName: selectedOption,
                                                optionValue,
                                                newValue: val,
                                                valueType: "image",
                                                productId: product.productId,
                                                productTitle: product.productTitle,
                                            });
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </Collapsible>
        </div>
    );
};

const SwatchesModalLoader = () => {
    return (
        <div className={styles.loaderContainer}>
            <div className={styles.loaderBox}>
                <Spinner accessibilityLabel="Loading product options" size="large" />
                <div className={styles.loaderTextGroup}>
                    <Text variant="headingMd" as="h2">
                        Loading Product Options
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                        We’re fetching the products you selected to show available swatches.
                    </Text>
                </div>
            </div>
        </div>
    );
};

const ImageSelector = ({
    imageUrl = "",
    onSelectImage,
}: {
    imageUrl?: string;
    onSelectImage: (imageUrl: string) => void;
}) => {
    const [mediaOpen, setMediaOpen] = useState(false);
    const [uploading, setUploading] = useState(false);

    const handleSelect = (selectedItems: any[]) => {
        if (selectedItems.length > 0) {
            const selectedImage = selectedItems[0]?.previewUrl;
            if (selectedImage) onSelectImage(selectedImage);
        }
        setMediaOpen(false);
    };

    const handleImageUpload = async (file: File) => {
        const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB

        if (!file.type.startsWith("image/")) {
            shopify.toast.show(`Only image files are allowed.`, { isError: true });
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            shopify.toast.show(`Image exceeds the 1MB file size limit.`, { isError: true });
            return; // stop upload if file too big
        }

        const data = new FormData();
        data.append("file", file);

        try {
            const res = await postData({
                url: `/${config.appName}/upload`,
                payload: data,
            });
            if (res.status == 200) {
                const uploadedUrl = res?.data?.url;
                if (uploadedUrl) {
                    onSelectImage(uploadedUrl);
                }
            } else {
                shopify.toast.show(`Failed to upload image`, { isError: true });
            }
        } catch (err) {
            console.error("Image upload failed", err);
            shopify.toast.show(`Failed to upload image`, { isError: true });
        }
    };

    return (
        <>
            <div className={styles.imageSelectorWrapper}>
                {/* Image Preview */}
                <div className={styles.imagePreview}>
                    {imageUrl ? (
                        <img src={imageUrl} alt="Preview" className={styles.image} />
                    ) : (
                        <div className={styles.placeholderIcon}>
                            <Icon source={ImageIcon} tone="subdued" />
                        </div>
                    )}
                </div>

                <span
                    style={{
                        width: "2.5rem",
                    }}
                >
                    <DropZone
                        allowMultiple={false}
                        accept="image/*"
                        onDrop={async (files) => {
                            const file = files[0];
                            if (file) {
                                setUploading(true);

                                await handleImageUpload(file);
                                setUploading(false);
                            }
                        }}
                        type="image"
                    >
                        {uploading ? (
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    padding: "8px",
                                }}
                            >
                                <Spinner accessibilityLabel="Uploading" size="small" />
                            </div>
                        ) : (
                            <DropZone.FileUpload actionTitle="" />
                        )}
                    </DropZone>
                </span>

                {/* Button to open media library */}
                <div className={styles.dropZoneMini}>
                    <Button size="slim" onClick={() => setMediaOpen(true)}>
                        Browse Library
                    </Button>
                </div>
            </div>

            {/* Media Library Modal */}

            {mediaOpen &&
                ReactDOM.createPortal(
                    <MediaLibrary
                        open={mediaOpen}
                        onClose={() => setMediaOpen(false)}
                        onSelect={handleSelect}
                        multiple={false}
                        restrictToImages={true}
                        title="Choose Image"
                    />,
                    document.body,
                )}
        </>
    );
};
